/**
 * Pulls the filename out of a `Content-Disposition` header.
 *
 * ASP.NET's `File(bytes, contentType, fileDownloadName)` emits *both* forms the spec allows:
 *
 *   attachment; filename=invoices_20260921.zip; filename*=UTF-8''invoices_20260921.zip
 *
 * so the obvious `split("filename=")[1]` keeps everything after the first one and the browser
 * saves `invoices_20260921.zip; filename_=UTF-8''invoices_20260921.zip`. Read to the parameter
 * separator instead, and prefer the RFC 5987 `filename*` since that is the one that survives
 * non-ASCII characters.
 */
export function fileNameFromContentDisposition(
  contentDisposition: string | null | undefined,
  fallback: string
): string {
  if (!contentDisposition) return fallback;

  // filename*=UTF-8''percent%20encoded — charset and language come first and are dropped.
  const extended = /filename\*\s*=\s*[^']*'[^']*'([^;]+)/i.exec(contentDisposition);
  if (extended) {
    const decoded = tryDecode(extended[1].trim());
    if (decoded) return decoded;
  }

  // filename="quoted value" or filename=token, stopping at the next parameter.
  const plain = /filename\s*=\s*("([^"]*)"|[^;]+)/i.exec(contentDisposition);
  if (plain) {
    const value = (plain[2] ?? plain[1]).trim();
    if (value) return value;
  }

  return fallback;
}

function tryDecode(value: string): string | null {
  try {
    return decodeURIComponent(value) || null;
  } catch {
    // A malformed escape sequence is not worth losing the download over.
    return value || null;
  }
}
